import { Font, StyleSheet } from '@react-pdf/renderer';

const isTest = process.env.Node_ENV === 'test';

Font.register({
  family: 'Titillium',
  fonts: [
    {
      src: '/assets/fonts/TitilliumWeb-Regular.ttf',
      fontWeight: 'normal',
    },
    {
      src: '/assets/fonts/TitilliumWeb-Light.ttf',
      fontWeight: 'normal',
      fontStyle: 'italic',
    },
    {
      src: '/assets/fonts/TitilliumWeb-Bold.ttf',
      fontWeight: 'bold',
    },
  ],
});

Font.register({
  family: 'Urbanist',
  fonts: [
    {
      src: '/assets/fonts/Urbanist-Regular.ttf',
      fontWeight: 'normal',
    },
    {
      src: '/assets/fonts/Urbanist-Bold.ttf',
      fontWeight: 'bold',
    },
    {
      src: '/assets/fonts/Urbanist-light.ttf',
      fontWeight: 'normal',
      fontStyle: 'italic',
    },
  ],
});

Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/',
});

export const pdfDefaultTextColour = '#03152D';
export const pdfDefaultBackgroundColour = 'white';
export const pdfFontFamily = isTest ? 'Sans Serif' : 'Urbanist';
export const pdfTitleMainFontSize = 28;
export const pdfTitleSubFontSize = 10;
export const pdfAsterixFontSize = '8pt';
export const pdfDisclaimerFontSize = '8pt';
export const pdfDefaultFontSize = 10;
export const pdfTableFootNoteFontSize = 10;
export const pdfTableHeaderColour = '#3A2050';
export const pdfTableHeaderTextColour = 'white';
export const tableSubHeaderColour = '#B2B2B2';
export const tableBorderWidth = 1;
export const tableBorderStyle = 'solid' as const; // 'solid' is the only style supported by react-pdf
export const tableBorderColour = '#03152D';
export const linkColour = '#4BBF7C';

export const borderStyles = {
  borderColor: tableBorderColour,
  borderStyle: tableBorderStyle,
};

const borderAllSides = {
  borderWidth: tableBorderWidth,
  ...borderStyles,
};

const cellDefaultPadding = {
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 1,
  paddingBottom: 1,
};

const borderWithNoTop = {
  borderTopWidth: 0,
  borderRightWidth: tableBorderWidth,
  borderBottomWidth: tableBorderWidth,
  borderLeftWidth: tableBorderWidth,
  ...borderStyles,
};

const styles = StyleSheet.create({
  page: {
    padding: '20px 10px 40px 10px', // https://github.com/diegomura/react-pdf/issues/1439#issuecomment-1321868253
    backgroundColor: pdfDefaultBackgroundColour,
    color: pdfDefaultTextColour,
    fontSize: pdfDefaultFontSize,
    fontFamily: pdfFontFamily,
    letterSpacing: 0.02,
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: '#555',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingLeft: 0,
    paddingRight: 10,
  },
  logo: {
    width: 200,
    height: 'auto',
    marginRight: 15,
  },
  textBold: { fontWeight: 'bold' },
  textContainer: {
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  textTop: {
    fontSize: pdfTitleMainFontSize,
    fontWeight: 'bold',
  },
  textAsterix: {
    fontSize: pdfAsterixFontSize,
    fontWeight: 'normal',
  },
  textBottom: {
    fontSize: pdfTitleSubFontSize,
    fontWeight: 'normal',
  },
  section: { paddingLeft: 10, paddingRight: 10 },
  table: {
    width: '100%',
    paddingLeft: 10,
    paddingRight: 10,
  },
  tableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  hr: {
    borderBottomColor: tableBorderColour,
    borderBottom: '1px solid',
    marginVertical: '10px',
  },
  tableHeaderReducedWidth: {
    ...borderAllSides,
    ...cellDefaultPadding,
    backgroundColor: pdfTableHeaderColour,
    color: pdfTableHeaderTextColour,
    fontWeight: 'bold',
    textAlign: 'left',
    width: '34%',
  },
  tableHeaderReducedWidthNoFill: {
    ...borderAllSides,
    ...cellDefaultPadding,
    color: pdfDefaultTextColour,
    textAlign: 'left',
    width: '66%',
  },
  tableHeaderFullWidth: {
    ...borderAllSides,
    ...cellDefaultPadding,
    backgroundColor: pdfTableHeaderColour,
    color: pdfTableHeaderTextColour,
    fontWeight: 'bold',
    textAlign: 'left',
    flexGrow: 1,
  },
  vesselDetailsTableHeader: {
    ...borderAllSides,
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: pdfTableHeaderColour,
    color: pdfTableHeaderTextColour,
    fontWeight: 'bold',
    textAlign: 'left',
  },
  vesselDetailsTableRow: {
    ...borderAllSides,
    borderTopWidth: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    textAlign: 'left',
  },
  tableSubHeaderFullWidth: {
    ...borderWithNoTop,
    ...cellDefaultPadding,
    backgroundColor: tableSubHeaderColour,
    color: pdfDefaultTextColour,
    flexGrow: 1,
    textAlign: 'left',
  },
  tableCellReducedWidth: {
    ...borderWithNoTop,
    ...cellDefaultPadding,
    width: '34%',
    textAlign: 'left',
  },
  tableCellFullWidth: {
    ...borderWithNoTop,
    ...cellDefaultPadding,
    flexGrow: 1,
    textAlign: 'left',
  },
  vesselDetailsTableCell: {
    ...cellDefaultPadding,
    textAlign: 'left',
  },
  link: { color: linkColour, textDecoration: 'none', fontWeight: 'bold' },
  mailToLink: {
    color: linkColour,
    textDecoration: 'underline',
    fontWeight: 'bold',
  },
  copyright: {
    paddingLeft: 5,
    paddingRight: 10,
    marginLeft: 5,
    flexDirection: 'row',
    fontWeight: 'bold',
    fontSize: '8pt',
  },
});

export default styles;
